import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "@/components/Marquee";
import MoonMark from "@/components/MoonMark";
import { Divider, TattooBanner, Glyph } from "@/components/Ornaments";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { useT, Rich } from "@/components/contentContext";

const CREDITS = [
  "Crysis 3",
  "Army of Two: Devil's Cartel",
  "Need for Speed Most Wanted 2012",
  "Fuse",
  "Medal of Honor Warfighter",
];

const Intro = () => {
  const t = useT();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const moonY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <main>
      <Seo
        title="Custom TTRPG & D&D Music — Intelligent Emotional Composition | Nox Harmonia"
        description="Intelligent emotional music — intentional and deliberate. Custom TTRPG ambience, boss themes and Curse of Strahd soundtracks for dungeon masters and indie games, by composer Paul Elliott Messier."
      />
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
        <div className="starfield absolute inset-0" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 80% 60% at 70% 30%, rgba(226,232,240,0.05), transparent 60%)" }}
          aria-hidden="true"
        />

        {/* parallax moon */}
        <motion.div
          style={{ y: moonY }}
          className="pointer-events-none absolute right-0 top-[8%] w-[62vw] max-w-[560px] opacity-60 md:right-[4%] md:top-[14%] md:w-[36vw] md:opacity-100"
          aria-hidden="true"
        >
          <div
            className="aspect-square w-full rounded-full object-cover"
            style={{
              backgroundImage: "url(/images/moon_hero.jpg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              animation: "moon-float 9s ease-in-out infinite, moon-glow 7s ease-in-out infinite",
            }}
          />
        </motion.div>

        <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 lg:px-10">
          <FadeIn delay={0.15}>
            <p className="eyebrow flex items-center gap-3">
              <Glyph className="h-3 w-3 text-gold" />
              {t("intro.hero.eyebrow")}
            </p>
          </FadeIn>

          <h1 className="mt-8 font-display font-bold leading-[0.98] text-silver">
            <MaskedLine delay={0.25} className="text-[14.5vw] sm:text-7xl md:text-8xl xl:text-[8.5rem]">
              NOX
            </MaskedLine>
            <MaskedLine delay={0.42} className="text-[14.5vw] sm:text-7xl md:text-8xl xl:text-[8.5rem]">
              HARMONIA
            </MaskedLine>
          </h1>

          <FadeIn delay={0.9}>
            <Rich
              as="p"
              text={t("intro.hero.subline")}
              className="mt-10 max-w-xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
            />
          </FadeIn>

          <FadeIn delay={1.05}>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/commission" data-testid="hero-cta-commission" className="btn-night">
                Commission Custom Music
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-portfolio" className="btn-ghost-night">
                Explore the Work
              </Link>
            </div>
          </FadeIn>
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
          aria-hidden="true"
        >
          <div className="flex flex-col items-center gap-3">
            <span className="font-jmono text-[10px] uppercase tracking-[0.4em] text-slate-600">Scroll</span>
            <div className="h-14 w-px overflow-hidden bg-line">
              <motion.div
                className="h-1/2 w-px bg-gold"
                animate={{ y: [0, 56] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      <Marquee />

      {/* ---------- THE OFFERING — what, who, why ---------- */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32 lg:px-10" data-testid="offering-section">
        <Reveal>
          <p className="eyebrow">The Offering</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
            {t("intro.offering.heading")}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {[
            { title: t("intro.offering.card1_title"), body: t("intro.offering.card1_body") },
            { title: t("intro.offering.card2_title"), body: t("intro.offering.card2_body") },
            { title: t("intro.offering.card3_title"), body: t("intro.offering.card3_body") },
          ].map((c, i) => (
            <Reveal key={c.title + i} delay={0.1 * i}>
              <div className="h-full border border-line bg-surface p-8 transition-colors duration-500 hover:border-gold/40">
                <MoonMark className="h-7 w-7 text-gold/80" />
                <h3 className="mt-6 font-display text-xl tracking-[0.1em] text-silver">{c.title}</h3>
                <Rich as="p" text={c.body} className="mt-4 text-sm font-light leading-relaxed text-slate-400" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <div className="mt-14 text-center">
            <Link to="/commission" data-testid="offering-cta-commission" className="btn-night">
              Commission Custom Music
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ---------- PORTRAITS ---------- */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-36 lg:px-10" data-testid="portrait-section">
        <Reveal>
          <p className="eyebrow">The Artist</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
            {t("intro.portraits.heading")}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <figure className="photo-frame group h-full">
              <div className="overflow-hidden">
                <img
                  src="/images/metal.webp"
                  alt="Nox Harmonia performing live with guitar"
                  data-testid="photo-live-main"
                  className="h-[460px] w-full object-cover transition-transform duration-700 group-hover:scale-[1.02] md:h-[560px]"
                  style={{ filter: "contrast(1.06) saturate(0.92)" }}
                />
              </div>
              <figcaption className="flex items-center justify-between px-3 py-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                <span>{t("intro.portraits.cap1")}</span>
                <MoonMark className="h-4 w-4 text-gold/60" />
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.12} className="md:col-span-5">
            <figure className="photo-frame group h-full">
              <div className="overflow-hidden">
                <img
                  src="/images/me4.webp"
                  alt="Nox Harmonia on stage, hooded, in front of the microphone"
                  data-testid="photo-live-second"
                  className="h-[460px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02] md:h-[560px]"
                  style={{ filter: "contrast(1.06) saturate(0.92)" }}
                />
              </div>
              <figcaption className="px-3 py-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                {t("intro.portraits.cap2")}
              </figcaption>
            </figure>
          </Reveal>

          <Reveal className="md:col-span-5">
            <figure className="photo-frame group h-full">
              <div className="overflow-hidden">
                <img
                  src="/images/artist_sunlit.jpg"
                  alt="Paul Elliott Messier smiling in the sun with an acoustic guitar"
                  data-testid="photo-sunlit"
                  className="h-[360px] w-full object-cover transition-transform duration-700 group-hover:scale-[1.02] md:h-[440px]"
                  style={{ filter: "contrast(1.05) saturate(0.95)" }}
                />
              </div>
              <figcaption className="px-3 py-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                {t("intro.portraits.cap3")}
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.12} className="md:col-span-7">
            <figure className="photo-frame group h-full">
              <div className="overflow-hidden">
                <img
                  src="/images/studio.webp"
                  alt="The home studio — synth, microphone and workstation"
                  data-testid="photo-studio"
                  className="h-[360px] w-full object-cover transition-transform duration-700 group-hover:scale-[1.02] md:h-[440px]"
                  style={{ filter: "contrast(1.04) brightness(0.94)" }}
                />
              </div>
              <figcaption className="flex items-center justify-between px-3 py-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                <span>{t("intro.portraits.cap4")}</span>
                <MoonMark className="h-4 w-4 text-gold/60" />
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <TattooBanner className="my-4" />

      {/* ---------- MANIFESTO ---------- */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-36 lg:px-10" data-testid="manifesto-section">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <MoonMark className="h-10 w-10 text-gold" />
              <p className="mt-8 font-cormorant text-3xl italic leading-snug text-silver md:text-4xl">
                "{t("intro.manifesto.quote")}"
              </p>
              <p className="mt-6 font-metal text-2xl text-gold/80">— Paul Elliott Messier</p>
              <p className="mt-2 font-jmono text-[10px] uppercase tracking-[0.35em] text-slate-500">
                {t("intro.manifesto.known_as")}
              </p>
            </Reveal>
          </div>
          <div className="space-y-6 text-base font-light leading-relaxed text-slate-400 lg:col-span-7">
            <Reveal delay={0.1}>
              <Rich as="p" text={t("intro.manifesto.body1")} />
            </Reveal>
            <Reveal delay={0.2}>
              <Rich as="p" text={t("intro.manifesto.body2")} />
            </Reveal>
            <Reveal delay={0.3}>
              <Rich
                as="p"
                text={t("intro.manifesto.closing")}
                className="font-cormorant text-xl italic text-slate-300"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- CREDITS TEASER ---------- */}
      <section className="border-t border-line bg-[#090910] py-24 md:py-32" data-testid="credits-teaser">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-10">
          <Reveal>
            <p className="eyebrow">AAA Credits</p>
            <p className="mt-6 font-jmono text-xs uppercase tracking-[0.4em] text-slate-500">
              {t("intro.credits.line")}
            </p>
          </Reveal>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {CREDITS.map((c, i) => (
              <Reveal key={c} delay={0.08 * i}>
                <span className="flex items-center gap-10">
                  <span className="font-display text-lg text-slate-300 md:text-2xl">{c}</span>
                  {i < CREDITS.length - 1 && <Glyph className="hidden h-3 w-3 text-gold/60 md:block" />}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Divider className="mx-auto my-14 max-w-md" />
            <Link to="/portfolio" data-testid="credits-cta-portfolio" className="btn-night">
              Walk the Gallery Wall
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Intro;
